import React from 'react';
import { Button, Dropdown, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, LockOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useLocation } from 'react-router-dom';
import { usePermission } from '../../hooks/usePermission';
import { isAdminPath } from '../../lib/adminPaths';
import { useAdminAuthStore } from '../../stores/adminAuthStore';

interface ListActionsDropdownProps {
  onDetail?: () => void;
  onEdit?: () => void;
  /** 編集に必要な権限レベル（デフォルト: 'edit'） */
  editPermission?: 'edit' | 'manage';
  additionalActions?: MenuProps['items'];
}

/**
 * リストページで使用する操作ドロップダウンコンポーネント
 *
 * 標準デザイン:
 * - …ボタン
 * - 詳細表示、編集などのアクション
 * - 削除ボタンはリストページには配置しない（詳細ページに配置）
 */
export const ListActionsDropdown: React.FC<ListActionsDropdownProps> = ({
  onDetail,
  onEdit,
  editPermission = 'edit',
  additionalActions = [],
}) => {
  const location = useLocation();
  const { canEdit, canManage, getDisabledMessage } = usePermission();
  const isAdminRoute = isAdminPath(location.pathname);
  const isAdminAuthenticated = useAdminAuthStore((state) => state.isAuthenticated);

  // 管理画面（/admin 配下）では通常ユーザー権限ストアとは別経路で認証されるため、編集権限判定を緩める。
  // （管理画面の詳細な権限制御は各ページ側の AdminAuthGuard で担保する）
  const effectiveCanEdit = isAdminRoute && isAdminAuthenticated ? true : canEdit;
  const effectiveCanManage = isAdminRoute && isAdminAuthenticated ? true : canManage;

  // 編集権限チェック
  const hasEditPermission = editPermission === 'manage' ? effectiveCanManage : effectiveCanEdit;
  const editDisabledMessage = getDisabledMessage(editPermission);

  const menuItems: MenuProps['items'] = [
    ...(onDetail ? [{
      key: 'detail',
      icon: <EyeOutlined />,
      label: '詳細表示',
      onClick: onDetail,
    }] : []),
    ...(onEdit ? [{
      key: 'edit',
      icon: hasEditPermission ? <EditOutlined /> : <LockOutlined />,
      label: hasEditPermission ? '編集' : (
        <Tooltip title={editDisabledMessage}>
          <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>編集</span>
        </Tooltip>
      ),
      onClick: hasEditPermission ? onEdit : undefined,
      disabled: !hasEditPermission,
    }] : []),
    ...additionalActions,
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={['click']}
    >
      <Button type="text" icon={<MoreOutlined />} />
    </Dropdown>
  );
};

export default ListActionsDropdown;
